import React, { useMemo } from 'react';
import icons, { baseDangerLinkClass } from 'constants/icons';
import propTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { toggleSidebar } from 'state/actions/site';
import { cn } from 'lib/utils';
import { withViewAsSearch } from 'helpers/viewAsAgentLink';
import useMobile from 'hooks/useMobile';
import useViewAs from 'hooks/useViewAs';
import { useSidebarNav } from './sidebarNavContext';

// geometry only — colors come from the theme strings below so the light/dark
// panels never fight each other over same-specificity utilities
const baseLink =
  'flex items-center !w-full min-w-0 relative gap-[5px] py-2.5 pl-[25px] transition-all duration-100 ' +
  '[&_svg]:!w-4 [&_svg]:!h-4 [&_svg]:shrink-0 ' +
  '[&_.sidebar-link-text]:text-[length:var(--font-size-body)] [&_.sidebar-link-text]:font-light [&_.sidebar-link-text]:min-h-0 [&_.sidebar-link-text]:pt-0.5 ' +
  'after:content-[""] after:absolute after:h-full after:w-0 after:top-0 after:right-0 after:opacity-0';

const lightIdleLink =
  'after:bg-primary-darkened [&_svg]:text-grey-light [&_.sidebar-link-text]:text-grey ' +
  'hover:bg-primary-light hover:[&_.sidebar-link-text]:text-primary-darkened hover:[&_svg]:text-primary-darkened';

const lightActiveLink =
  'after:bg-primary-darkened bg-primary-light [&_.sidebar-link-text]:text-primary-darkened [&_.sidebar-link-text]:font-normal [&_svg]:text-primary-darkened after:w-[5px] after:opacity-100';

const darkIdleLink =
  'after:bg-white [&_svg]:text-white/55 [&_.sidebar-link-text]:text-white/75 ' +
  'hover:bg-white/10 hover:[&_.sidebar-link-text]:text-white hover:[&_svg]:text-white';

const darkActiveLink =
  'after:bg-white bg-white/15 [&_.sidebar-link-text]:text-white [&_.sidebar-link-text]:font-normal [&_svg]:text-white after:w-[5px] after:opacity-100';

// picks the color set for the panel the link renders in, idle or active
function linkThemeClass(isDarkPanel, active) {
  if (isDarkPanel) {
    return active ? darkActiveLink : darkIdleLink;
  }
  return active ? lightActiveLink : lightIdleLink;
}

const extraIconBaseClass = '!w-4 !h-4 !min-w-4 !min-h-4 !mr-0 shrink-0';

const dangerLink =
  `${baseDangerLinkClass} ` +
  '[&_.sidebar-link-text]:!text-[var(--color-semantic-danger)] hover:[&_.sidebar-link-text]:!text-[var(--color-semantic-danger)]';

// pending work: amber text and icons, the same accent the warning notices use
const warningLink =
  '[&_svg]:!text-amber-600 hover:[&_svg]:!text-amber-600 ' +
  '[&_.sidebar-link-text]:!text-amber-600 hover:[&_.sidebar-link-text]:!text-amber-600';

const iconWrapperBase =
  'flex shrink-0 w-4 h-4 !mr-1 items-center justify-center';

function MenuLink({
  icon = '',
  text,
  className,
  active,
  to = '/',
  external,
  extraIcon = '',
  tabType = 'default',
  iconFill = false,
  extraIconFill = false,
  badge = null,
  ...linkProps
}) {
  const Icon = icons[icon] || icons.home;
  const ExtraIcon = icons[extraIcon] || null;
  const dispatch = useDispatch();
  const { isShrinked, isDarkPanel } = useSidebarNav();
  const { targetId: viewAsAgent } = useViewAs();

  const resolvedTo = useMemo(
    () => (external ? to : withViewAsSearch(to, viewAsAgent)),
    [external, to, viewAsAgent]
  );

  const isMobile = useMobile();

  const handleToggleSideBar = () => {
    if (isMobile) dispatch(toggleSidebar(false));
  };

  const linkClassName = cn(
    baseLink,
    linkThemeClass(isDarkPanel, active),
    isShrinked && '!pl-0 justify-center',
    tabType === 'danger' && dangerLink,
    tabType === 'warning' && warningLink,
    className
  );

  const iconWrapperClassName = cn(iconWrapperBase, isShrinked && '!mr-0');

  const isDanger = tabType === 'danger';
  const isWarning = tabType === 'warning';
  const dangerFill = 'rgba(220,38,38,0.15)';

  const content = (
    <>
      <span className={iconWrapperClassName}>
        <Icon
          fill={iconFill ? (isDanger ? dangerFill : 'currentColor') : 'none'}
        />
      </span>
      <span className='sidebar-link-text'>{text}</span>
      {extraIcon && ExtraIcon && (
        <ExtraIcon
          className={cn(
            extraIconBaseClass,
            // #266fba reads too dark against the navy panel
            isDarkPanel ? 'text-primary-emphasis' : 'text-[#266fba]',
            isDanger && '!text-semantic-danger',
            isWarning && '!text-amber-600'
          )}
          fill={
            extraIconFill ? (isDanger ? dangerFill : 'currentColor') : 'none'
          }
        />
      )}
      {badge && !isShrinked && <span className='ml-1 flex'>{badge}</span>}
    </>
  );

  return external ? (
    <a title={text} className={linkClassName} href={to} {...linkProps}>
      {content}
    </a>
  ) : (
    <Link
      title={text}
      className={linkClassName}
      to={resolvedTo}
      onClick={handleToggleSideBar}
      {...linkProps}
    >
      {content}
    </Link>
  );
}

MenuLink.propTypes = {
  icon: propTypes.string,
  text: propTypes.string,
  active: propTypes.bool,
  external: propTypes.bool,
  to: propTypes.string,
  className: propTypes.string,
  extraIcon: propTypes.string,
  tabType: propTypes.oneOf(['default', 'danger', 'warning']),
  iconFill: propTypes.bool,
  extraIconFill: propTypes.bool,
  badge: propTypes.node
};

export default React.memo(MenuLink);
